/**
 * runidea.ai — Phase 16 — Compare All Strategies modal.
 *
 * Pro-tier paywalled. Renders the same property's Rental / Flip / BRRRR
 * underwriting side-by-side, highlights the recommended strategy, and
 * surfaces an AI-generated primary + fallback summary.
 */
import { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Trophy, Sparkles, Microscope, RotateCcw, Lock, ExternalLink } from "lucide-react";
import VerdictBadge from "@/components/VerdictBadge";
import DealTypePill, { DEAL_TYPE_META } from "@/components/deal/DealTypePill";
import { useFmt } from "@/lib/useFmt";

const STRATEGY_ORDER = ["rental", "flip", "brrrr"];

const ACCENT_BG = {
  rental: "bg-emerald-500",
  flip: "bg-orange-500",
  brrrr: "bg-purple-500",
};

function fmtPct(v, digits = 1) {
  if (v == null || isNaN(v)) return "—";
  return `${Number(v).toFixed(digits)}%`;
}

/* -------------------------------------------------------------------------
 * Upgrade-to-Pro paywall overlay — shown when the API returns 402.
 * --------------------------------------------------------------------- */
export function UpgradeProModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-pro-modal" className="max-w-md">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Lock className="h-5 w-5" />
          </div>
          <DialogTitle className="text-2xl">Pro feature</DialogTitle>
          <DialogDescription className="pt-1 text-sm text-slate-600">
            See this property analyzed as <strong>Rental</strong>, <strong>Flip</strong>, AND <strong>BRRRR</strong> — side by side.
          </DialogDescription>
        </DialogHeader>
        <ul className="mt-2 space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
            <span>3 underwriting models on one screen — no manual re-entry.</span>
          </li>
          <li className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
            <span>AI recommends the best primary strategy + fallback.</span>
          </li>
          <li className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
            <span>Saves 2-3 hours per deal versus running three separate analyses.</span>
          </li>
        </ul>
        <DialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
          <Button
            asChild
            data-testid="upgrade-pro-cta"
            className="w-full bg-indigo-600 text-white hover:bg-indigo-700 sm:flex-1"
          >
            <a href="/upgrade" rel="noreferrer">
              Upgrade to Pro <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </a>
          </Button>
          <button
            type="button"
            data-testid="upgrade-pro-retry"
            onClick={() => onOpenChange(false)}
            className="text-xs text-slate-500 underline hover:text-slate-700"
          >
            Already Pro? Try refreshing.
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------
 * Strategy card — one column per type.
 * --------------------------------------------------------------------- */
function StrategyCard({ k, strategy, isRecommended, fmt }) {
  const meta = DEAL_TYPE_META[k] || DEAL_TYPE_META.rental;
  const kpis = strategy?.kpis || {};
  const extras = strategy?.extras || {};
  const headline = strategy?.headline || {};
  return (
    <div
      data-testid={`strategy-card-${k}`}
      className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all ${
        isRecommended ? "border-indigo-400 ring-2 ring-indigo-200" : "border-slate-200"
      }`}
    >
      <div className={`h-1.5 w-full ${ACCENT_BG[k]}`} />
      {isRecommended && (
        <div
          data-testid={`strategy-card-${k}-recommended`}
          className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow"
        >
          <Trophy className="h-3 w-3" /> Recommended
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center gap-2">
          <DealTypePill type={k} size="md" />
          <span className="text-xs text-slate-500">Composite {Number(strategy.composite_score || 0).toFixed(2)}</span>
        </div>

        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {headline.label || "Headline"}
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">
            {headline.format === "percent" ? fmtPct(headline.value, 1) : fmt.money(headline.value)}
          </p>
          {/* Phase 47.3 — normalised PSF/PSM per strategy. Rental
              anchors on purchase price; flip + BRRRR anchor on ARV
              (the value at exit / refi). Skipped when sqft is unset. */}
          {strategy.price_per_sqft != null && strategy.price_per_sqm != null ? (
            <p
              data-testid={`strategy-card-${k}-psf`}
              className="mt-1 text-[10px] text-slate-400 tabular-nums"
            >
              ${Math.round(strategy.price_per_sqft).toLocaleString()}/sqft · ${Math.round(strategy.price_per_sqm).toLocaleString()}/sqm
            </p>
          ) : null}
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">Deal score</dt>
            <dd className="text-xl font-bold tabular-nums text-slate-900">{kpis.deal_score != null ? Math.round(kpis.deal_score) : "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">Verdict</dt>
            <dd className="mt-1"><VerdictBadge verdict={strategy.verdict} /></dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">Cash flow / mo</dt>
            <dd className={`text-sm font-semibold tabular-nums ${(kpis.cash_flow_monthly || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
              {fmt.money(kpis.cash_flow_monthly, { cents: true })}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">DSCR</dt>
            <dd className="text-sm font-semibold tabular-nums text-slate-900">
              {kpis.dscr != null ? Number(kpis.dscr).toFixed(2) : "n/a"}
            </dd>
          </div>
        </dl>

        {/* Type-specific row */}
        <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {k === "rental" && (
            <>Annual cash flow: <strong className="text-slate-900">{fmt.money(extras.cash_flow_annual)}</strong></>
          )}
          {k === "flip" && (
            <>Gross profit: <strong className="text-slate-900">{fmt.money(extras.gross_profit_total, { cents: true })}</strong>
            <span className="ml-2 text-slate-500">({fmtPct(extras.annualized_roi_pct, 1)} annualized)</span></>
          )}
          {k === "brrrr" && (
            <>Cash recycled: <strong className="text-slate-900">{fmt.money(extras.cash_recycled)}</strong>
            {extras.infinite_return_flag ? (
              <span className="ml-2 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-purple-700">∞ return</span>
            ) : (
              <span className="ml-2 text-slate-500">Cash left {fmt.money(extras.cash_left_in_deal)}</span>
            )}</>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Main modal.
 * --------------------------------------------------------------------- */

/**
 * Phase 21 — Sensitivity matrix (3×3) showing how `deal_score` shifts when
 * ARV moves ±10% (rows) and repair budget moves ±20% (columns). Helps the
 * user spot whether the recommended strategy still wins in stressed
 * scenarios. Color-coded: emerald if cell ≥ baseline, amber if -10 below,
 * rose if more than -10 below. Baseline cell is highlighted with a ring.
 */
function SensitivityMatrix({ strategy, matrix, baselineDealScore }) {
  if (!matrix || !matrix.cells) return null;
  const baseScore = Number(baselineDealScore || 0);
  const cellTone = (score) => {
    const v = Number(score || 0);
    if (v >= baseScore) return "bg-emerald-50 border-emerald-200 text-emerald-900";
    if (v >= baseScore - 10) return "bg-amber-50 border-amber-200 text-amber-900";
    return "bg-rose-50 border-rose-200 text-rose-900";
  };
  return (
    <div
      data-testid={`compare-sensitivity-${strategy}`}
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Sensitivity · {strategy.toUpperCase()}
        </p>
        <p className="mt-0.5 text-xs text-slate-600">
          Deal score across ARV ±10% × repair ±20%. Baseline highlighted.
        </p>
      </div>
      <div className="p-3">
        <table className="w-full table-fixed border-collapse text-center text-xs">
          <thead>
            <tr>
              <th className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                ARV \ Repair
              </th>
              {matrix.axes.repair_pcts.map((rp) => (
                <th
                  key={`th-${rp}`}
                  className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                >
                  {rp > 0 ? `+${rp}%` : `${rp}%`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.cells.map((row, rowIdx) => {
              const arvPct = matrix.axes.arv_pcts[rowIdx];
              return (
                <tr key={`row-${rowIdx}`}>
                  <td className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {arvPct > 0 ? `+${arvPct}%` : `${arvPct}%`}
                  </td>
                  {row.map((cell, colIdx) => (
                    <td
                      key={`cell-${rowIdx}-${colIdx}`}
                      data-testid={`sens-cell-${strategy}-${arvPct}-${matrix.axes.repair_pcts[colIdx]}`}
                      className={`border ${cellTone(cell.deal_score)} ${
                        cell.is_baseline ? "ring-2 ring-indigo-500 ring-offset-1" : ""
                      } px-2 py-2 align-middle font-mono`}
                    >
                      <div className="text-sm font-bold tabular-nums">
                        {cell.deal_score != null ? Math.round(cell.deal_score) : "—"}
                      </div>
                      <div className="text-[9px] uppercase tracking-wider opacity-70">
                        {cell.verdict || "—"}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CompareStrategiesModal({ open, onOpenChange, deal }) {
  const fmt = useFmt();
  const [data, setData] = useState(deal?.strategy_comparison || null);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Adjustable assumptions panel.
  const [form, setForm] = useState({
    arv: deal?.arv || deal?.purchase_price ? Math.round((deal?.arv || deal?.purchase_price || 0) * 1.2) : 0,
    repair_budget: deal?.repair_budget || deal?.repairs || 0,
    refi_ltv_pct: deal?.refi_ltv_pct || 75,
    refi_interest_rate: deal?.refi_interest_rate || 7.0,
    holding_period_months: deal?.holding_period_months || 6,
  });

  const run = useCallback(async (overrides) => {
    setLoading(true);
    try {
      const body = { ...form, ...(overrides || {}) };
      const { data } = await api.post(`/deals/${deal.id}/compare-strategies`, body);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowUpgrade(true);
      } else {
        toast.error(e?.response?.data?.detail || "Compare failed");
      }
    } finally {
      setLoading(false);
    }
  }, [deal?.id, form]);

  // Auto-run on first open if we don't have a cached snapshot.
  useEffect(() => {
    if (open && !data && deal?.id) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal?.id]);

  const rec = data?.recommended_strategy;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="compare-strategies-modal"
          className="max-h-[92vh] w-[95vw] max-w-5xl overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Microscope className="h-5 w-5 text-indigo-600" /> Strategy Comparison
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {deal?.property?.address || "this property"} — analysed three ways.
            </DialogDescription>
          </DialogHeader>

          {/* Quick-adjust form */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Quick adjust assumptions
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">ARV</Label>
                <Input data-testid="compare-arv" type="number" min={0}
                       value={form.arv} onChange={(e) => setForm({ ...form, arv: Number(e.target.value) })}
                       className="h-9 mt-1 text-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Repair</Label>
                <Input data-testid="compare-repair" type="number" min={0}
                       value={form.repair_budget} onChange={(e) => setForm({ ...form, repair_budget: Number(e.target.value) })}
                       className="h-9 mt-1 text-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Flip mo.</Label>
                <Input data-testid="compare-hold" type="number" min={1}
                       value={form.holding_period_months} onChange={(e) => setForm({ ...form, holding_period_months: Number(e.target.value) })}
                       className="h-9 mt-1 text-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Refi LTV</Label>
                <Input data-testid="compare-refi-ltv" type="number" min={10} max={90}
                       value={form.refi_ltv_pct} onChange={(e) => setForm({ ...form, refi_ltv_pct: Number(e.target.value) })}
                       className="h-9 mt-1 text-sm tabular-nums" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Refi rate</Label>
                <Input data-testid="compare-refi-rate" type="number" step="0.1" min={0}
                       value={form.refi_interest_rate} onChange={(e) => setForm({ ...form, refi_interest_rate: Number(e.target.value) })}
                       className="h-9 mt-1 text-sm tabular-nums" />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                onClick={() => run()}
                disabled={loading}
                data-testid="compare-rerun"
                className="bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
                Re-run
              </Button>
            </div>
          </div>

          {/* Cards grid */}
          {loading && !data ? (
            <div className="py-16 text-center text-slate-400">
              <Loader2 className="mx-auto h-6 w-6 animate-spin" />
              <p className="mt-2 text-sm">Running three underwriting models…</p>
            </div>
          ) : data ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3" data-testid="compare-cards-grid">
                {STRATEGY_ORDER.map((k) => (
                  <StrategyCard key={k} k={k} strategy={data.strategies?.[k] || {}} isRecommended={rec === k} fmt={fmt} />
                ))}
              </div>

              {/* Phase 21 — Sensitivity matrices for flip + brrrr (rental is null). */}
              {data.sensitivity ? (
                <div className="grid gap-4 sm:grid-cols-2" data-testid="compare-sensitivity-grid">
                  {["flip", "brrrr"].map((k) =>
                    data.sensitivity[k] ? (
                      <SensitivityMatrix
                        key={`sens-${k}`}
                        strategy={k}
                        matrix={data.sensitivity[k]}
                        baselineDealScore={data.strategies?.[k]?.deal_score}
                      />
                    ) : null
                  )}
                </div>
              ) : null}

              {/* AI verdict panel */}
              <div
                data-testid="compare-ai-summary"
                className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-5 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-700">AI strategy verdict</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{data.ai_summary}</p>
                <p className="mt-3 text-[10px] font-mono text-slate-400">
                  Composite: rental {data.composite_scores?.rental?.toFixed(2)} · flip {data.composite_scores?.flip?.toFixed(2)} · brrrr {data.composite_scores?.brrrr?.toFixed(2)}
                </p>
              </div>
            </>
          ) : (
            <p className="py-8 text-center text-sm text-slate-400">No comparison data yet.</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compare-close">Close</Button>
            <Button
              onClick={() => {
                // Phase 20 — Open the share PDF in a new tab. Backend logs
                // a `verdict_pdf_shared` analytics event for attribution.
                const url = `${API_BASE}/deals/${deal.id}/share-verdict-pdf?mode=compare`;
                window.open(url, "_blank", "noopener,noreferrer");
                toast.success("PDF opened in a new tab — share it with anyone");
              }}
              variant="outline"
              disabled={!data}
              data-testid="compare-share-pdf"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Share as PDF
            </Button>
            <Button
              onClick={() => { toast.success("Saved to deal — re-opens are instant"); onOpenChange(false); }}
              disabled={!data}
              data-testid="compare-save"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Save & close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UpgradeProModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </>
  );
}
